import { useEffect, useState } from "react";
import middleware from "../../Api/Middleware";
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { formatCurrency, parseCurrency } from "../../../utils";
import { PencilIcon } from 'lucide-react';
import { BorderedDropDown } from "../../HouseStyle/Components/DropDowns";
import ToastError from "../../Toasts/ToastError";
import ToastSuccess from "../../Toasts/ToastSuccess";
import { ButtonLightGrey } from "../../HouseStyle/Components/Buttons";

const BetalingComponent = ({ order, setPayRequestModalVisible, setPaymentId, fetchAll }) => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [changePaymentStatus, setChangePaymentStatus] = useState({});

    const fetchMethods = async () => {
        try {
            const data = await middleware.get('/payment/methods');
            setPaymentMethods(data.data.data);
        } catch (error) {
            console.error('Error fetching methods:', error);
        }
    };

    useEffect(() => {
        fetchMethods();
    }, []);

    const amountPaid = (order, type) => {
        let total = 0;

        if (type === 'payments' && order && order.payments && Array.isArray(order.payments)) {
            total = order.payments
                .filter(payment => payment.status === 'PAID')
                .reduce((sum, payment) => sum + Number(payment.amount), 0);
        }
        else if (type === 'creditations' && order && order.creditations && Array.isArray(order.creditations)) {
            total = order.creditations
                .filter(creditation => creditation.payment && creditation.payment.status === 'PAID')
                .reduce((sum, creditation) => sum + Number(creditation.payment.amount), 0);
        }
        return total;
    };

    const calculateCreditationTotal = (includeRestitutionCost = true) => {
        let restitution = order.creditations.reduce((total, creditation) => {
            const restitutionAmount = parseCurrency(creditation.restitution_total);
            return total + restitutionAmount;
        }, 0);

        if (includeRestitutionCost) restitution -= calculateCreditationCost();

        return restitution;
    };

    const calculateCreditationCost = () => {
        return order.creditations.reduce((total, creditation) => {
            const restitutionCost = parseCurrency(creditation.restitution_cost);
            return total + restitutionCost;
        }, 0);
    }

    const calculateTotal = () => {
        const originalTotal = order.orders_sold_products.reduce((sum, product) => {
            const price = product.price_incl_tax;
            return sum + (Number(price) * Number(product.amount));
        }, 0);
        
        return originalTotal;
    };

    const calculateRowTotal = (item) => {
        if (item.sale_unit === 1) {
            const discountAmount = Math.min(item.price_incl_tax, parseFloat(item.sale_amount) || 0);
            const priceAfterDiscount = Math.max(0, item.price_incl_tax - discountAmount);

            return priceAfterDiscount * item.amount;
        }
        else {
            const percentageDiscount = Math.min(100, parseFloat(item.sale_amount) || 0);
            const priceAfterDiscount = item.price_incl_tax * (1 - percentageDiscount / 100);

            return priceAfterDiscount * item.amount;
        }
    };

    const [values, setValues] = useState({});
    const CalculateValues = () => {
        if (order.orders_sold_products?.length) {
            const subtotal = order.orders_sold_products.reduce((sum, item) =>
                sum + calculateRowTotal(item), 0);

            // The discount over all the products.
            const totalDiscount = order.orders_sold_products.reduce((sum, product) => {
                const regularPrice = parseCurrency(product.price_incl_tax);
                const quantity = parseInt(product.amount) || 1;

                let discountPerItem = 0;
                if (product.sale_unit === 0) {
                    // Fixed amount discount (euro)
                    discountPerItem = Number(product.sale_amount);
                } else {
                    // Percentage discount - calculate based on original total
                    const discountPercentage = Number(product.sale_amount);
                    
                    // Calculate discount amount: original_price * (percentage / 100)
                    discountPerItem = regularPrice * (discountPercentage / 100);
                }

                return sum + (discountPerItem * quantity);
            }, 0);

            // The extra over the whole order
            let extraDiscount = 0;
            if (order.sale_unit === 0) {
                // Fixed amount discount (euro)
                extraDiscount = Number(order.sale_amount);
            } else {
                // Percentage discount - calculate based on original total
                const discountPercentage = Number(order.sale_amount);
                const originalTotal = (calculateTotal() - totalDiscount); // This should be the original price before any discounts
                
                // Calculate discount amount: original_price * (percentage / 100)
                extraDiscount = originalTotal * (discountPercentage / 100);
            }

            setValues({ subtotal, discount: totalDiscount, extra_discount: extraDiscount });
        }
    }

    useEffect(() => {
        CalculateValues();
    }, [order]);

    const PricesPaymentHistory = () => {
        const totalTransactionPaid = amountPaid(order, 'payments');
        // Sum restitution_total for all creditations with payment_completed = 1
        const totalCreditationPaid = order.creditations && Array.isArray(order.creditations)
            ? order.creditations.filter(cred => cred.payment_completed === 1 || cred.payment_completed === true)
                .reduce((sum, cred) => sum + (parseCurrency(cred.restitution_total) || 0), 0)
            : 0;
        let leftOverPrice = order.total_price_incl_tax - totalTransactionPaid - totalCreditationPaid;
        if (leftOverPrice < 0) leftOverPrice = 0;

        return (
            <div className="d-flex justify-content-between bottom-border-gray pb-2 pt-2">
                <div className="d-flex flex-column">
                    <span className="price-title">Openstaand</span>
                    <span className="price-title">Betaald</span>
                    <span className="price-title">Terugbetaald</span>
                </div>
                <div className="d-flex flex-column">
                    <span className="price">{formatCurrency(leftOverPrice)}</span>
                    <span className="price">{formatCurrency(totalTransactionPaid)}</span>
                    <span className="price">{formatCurrency(totalCreditationPaid)}</span>
                </div>
            </div>
        );
    };

    const paymentStatus = (status) => {
        switch (status) {
            case 'CANCEL':
                return <span>Geannuleerd</span>
            case 'PENDING':
                return <span>Openstaand</span>
            case 'PAID':
                return <span>Betaald</span>
            default:
                return <span>Onbekende status</span>
        }
    }

    const handlePaymentStatusChange = async paymentId => {
        try {
            const status = changePaymentStatus[paymentId];

            await middleware.post(`payments/changeStatus/${paymentId}`, {
                status
            });

            ToastSuccess(`De betaalstatus is successvol veranderd!`);
            setChangePaymentStatus({});
            fetchAll();
        } catch (error) {
            ToastError(`Er is iets fout gegaan tijdens het veranderen van de betaalstatus.`);
        }
    }

    return (
        <>
            <div className="whiteBox p-5 pb-4 pt-4 align-center">
                <div className="d-flex justify-content-between">
                    <h5 className="mb-4 fw-bold BlockTitle" style={{ color: "#A9D974" }}>Betaling</h5>
                    {(order.payment_status !== 5 && order.order_status > 1) && ( // Only show the pay button when the payment is not already completed and the order is not a concept.
                        <ButtonLightGrey text={'Bestelling betalen'} onClickFunction={() => setPayRequestModalVisible(true)} />
                    )}
                </div>

                <div className={`d-flex justify-content-between ${order.creditations.length ? 'mb-4' : 'mb-0' }`}>
                    <div className="col-6 ps-0 pe-0 betaling-overzicht-container">
                        <div className="fw-bold betaling-overzicht-container-headers">
                            <p className="ms-3 pt-2 pb-2">Bestelling overzicht</p>
                        </div>
                        <div className="ms-3 me-3 mb-1">
                            <div className="bottom-border-gray pb-2">
                                <div className="d-flex justify-content-between pb-1">
                                    <div>Totaal</div>
                                    <div>{formatCurrency((calculateTotal()))}</div>
                                </div>
                                {values.discount > 0 && (
                                    <div className="d-flex justify-content-between pb-1">
                                        <div>Korting</div>
                                        <div className="d-flex gap-2 text-danger">
                                            <div>- {formatCurrency(Number(values.discount))}</div>
                                        </div>
                                    </div>   
                                )}
                                {values.extra_discount > 0 && (
                                    <div className="d-flex justify-content-between pb-1">
                                        <div>Extra korting</div>
                                        <div className="d-flex gap-2 text-danger">
                                            <div>- {formatCurrency(Number(values.extra_discount))}</div>
                                        </div>
                                    </div>
                                )}
                                {(order.shipment_method === 1) && (
                                    <>
                                        <div className="d-flex justify-content-between pb-1 shipmentPrice">
                                            <div className="shipmentPrice">Verzendkosten</div>
                                            <div>{formatCurrency(Number(order.delivery_cost))}</div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="bottom-border-gray pt-2 pb-2">
                                <div className="d-flex justify-content-between fw-bold pb-3">
                                    <div>Subtotaal</div>
                                    <div>{formatCurrency(order.total_price_incl_tax)}</div>
                                </div>
                                <div className="d-flex justify-content-between pb-1">
                                    <div>Totaal excl. BTW</div>
                                    <div>{formatCurrency(order.total_price_excl_tax)}</div>
                                </div>
                                <div className="d-flex justify-content-between pb-2">
                                    <div>BTW</div>
                                    <div className="d-flex gap-4">
                                        <div>NL 21%</div>
                                        <div>{formatCurrency(order.total_price_excl_tax * 0.21)}</div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="d-flex justify-content-between fw-bold pt-2 pb-4">
                                    <div>Totaal incl. BTW</div>
                                    <div>
                                        {formatCurrency(order.total_price_incl_tax)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-6 ps-0 pe-0 betaling-overzicht-container">
                        <div className="fw-bold betaling-overzicht-container-headers">
                            <p className="ms-3 pt-2 pb-2 mb-0">Betaal historie</p>
                        </div>
                        <div className="ms-3 me-3">
                            {order.payments.length > 0 &&
                                order.payments.map((payment, index) => (
                                    <div key={index} className="bottom-border-gray py-2">
                                        <div className="d-flex justify-content-between">
                                            <div>Bestelling: #{order.order_number}</div>
                                            <div className="text-nowrap">{formatCurrency(payment.amount)}</div>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <div>Betalingsmethode: {
                                                Array.isArray(paymentMethods) ?
                                                    (paymentMethods.find(method => method.payment_method_id === payment.payment_method_id)?.method_name) :
                                                    payment.payment_method_id
                                            }</div>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                {payment.created_at ? (
                                                    format(
                                                        new Date(
                                                            payment.created_at.replace(/(\.\d{3})\d*Z$/, '$1Z')
                                                        ),
                                                        'dd MMMM yyyy HH:mm',
                                                        { locale: nl }
                                                    )
                                                ) : (
                                                    'Datum onbekend'
                                                )}
                                            </div>

                                            <div className="d-flex align-items-center">
                                                {changePaymentStatus[payment.payment_id] ? (
                                                    <>
                                                        <BorderedDropDown
                                                            selectedValue={changePaymentStatus[payment.payment_id]}
                                                            setSelectedValue={newStatus => setChangePaymentStatus(prev => ({...prev, [payment.payment_id]: newStatus}))}
                                                            options={[
                                                                { title: 'Annuleren', value: 'CANCEL' },
                                                                { title: 'Openstaand', value: 'PENDING' },
                                                                { title: 'Betaald', value: 'PAID' },
                                                            ]}
                                                        />
                                                        <PencilIcon
                                                            onClick={() => handlePaymentStatusChange(payment.payment_id)}
                                                            style={{cursor: 'pointer', marginLeft: '8px'}}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        {paymentStatus(payment.status)}
                                                        {payment.status === 'PENDING' && 
                                                        paymentMethods.find(method => method.payment_method_id === payment.payment_method_id)?.manual_editable == true && (
                                                            <PencilIcon
                                                                onClick={() => setChangePaymentStatus(prev => ({...prev, [payment.payment_id]: payment.status}))}
                                                                style={{cursor: 'pointer', marginLeft: '8px'}}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {payment.status !== 'PAID' && payment.status !== 'CANCEL' && (
                                            <div className="d-flex justify-content-between">
                                                <a href="#" className="new-link-color text-decoration-underline" onClick={() => {
                                                    setPayRequestModalVisible(true);
                                                    setPaymentId(payment.payment_id);
                                                }}>Link naar betaalverzoek</a>
                                            </div>
                                        )}
                                    </div>
                                ))
                            }

                            {/* Terugbetaling */}
                            {order.creditations.length > 0 &&
                                order.creditations.filter(creditation => creditation.payment && creditation.payment.status === 'PAID')
                                    .map((creditation, index) => (
                                        <div key={creditation.creditation_id || index} className="bottom-border-gray pb-2 pt-2">
                                            <div className="d-flex justify-content-between">
                                                <div>Bestelling: #{order.order_number}</div>
                                                <span className="text-nowrap" style={{ color: "red" }}>
                                                    - {formatCurrency(creditation.payment.amount)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <div>Betalingsmethode: {
                                                    creditation.payment.payment_method_id ?
                                                        (Array.isArray(paymentMethods) ?
                                                            (paymentMethods.find(method => method.payment_method_id === creditation.payment.payment_method_id)?.method_name || creditation.payment.payment_method_id) :
                                                            creditation.payment.payment_method_id) :
                                                        'N.v.t.'
                                                }</div>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    {creditation.created_at ? (
                                                        format(
                                                            new Date(
                                                                creditation.created_at.replace(/(\.\d{3})\d*Z$/, '$1Z')
                                                            ),
                                                            'dd MMMM yyyy HH:mm',
                                                            { locale: nl }
                                                        )
                                                    ) : (
                                                        'Datum onbekend'
                                                    )}
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <a
                                                    href="#"
                                                    className="new-link-color text-decoration-underline"
                                                    onClick={() => {
                                                        setPayRequestModalVisible(true);
                                                        setPaymentId(creditation.payment.payment_id);
                                                    }}
                                                >
                                                    Link naar betaalverzoek
                                                </a>
                                            </div>
                                        </div>
                                    ))
                            }
                            {PricesPaymentHistory()}
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between">
                    {order.creditations.length > 0 && (
                        <div className="col-6 ps-0 pe-0 betaling-overzicht-container">
                            <div className="fw-bold betaling-overzicht-container-headers">
                                <p className="ms-3 pt-2 pb-2">Retour overzicht</p>
                            </div>
                            <div className="ms-3 me-3 mb-1">
                                <div className="bottom-border-gray pb-2">
                                    <div className="d-flex justify-content-between pb-1">
                                        <div>Totaal</div>
                                        <div>{formatCurrency((calculateCreditationTotal(false)))}</div>
                                    </div>
                                    {calculateCreditationCost() > 0 && (
                                        <div className="d-flex justify-content-between pb-1">
                                            <div>Restitutiekosten</div>
                                            <div className="d-flex gap-2 text-danger">
                                                <div>- {formatCurrency(calculateCreditationCost())}</div>
                                            </div>
                                        </div>   
                                    )}
                                </div>
                                <div className="bottom-border-gray pt-2 pb-2">
                                    <div className="d-flex justify-content-between fw-bold pb-3">
                                        <div>Subtotaal</div>
                                        <div>{formatCurrency(calculateCreditationTotal())}</div>
                                    </div>
                                    <div className="d-flex justify-content-between pb-1">
                                        <div>Totaal excl. BTW</div>
                                        <div>{formatCurrency(calculateCreditationTotal() / 1.21)}</div>
                                    </div>
                                    <div className="d-flex justify-content-between pb-2">
                                        <div>BTW</div>
                                        <div className="d-flex gap-4">
                                            <div>NL 21%</div>
                                            <div>{formatCurrency((calculateCreditationTotal() / 1.21) * 0.21)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="d-flex justify-content-between fw-bold pt-2 pb-4">
                                        <div>Totaal incl. BTW</div>
                                        <div>
                                            {formatCurrency(calculateCreditationTotal())}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

            </div>
        </>
    )
}
export default BetalingComponent;